import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { ButtonWithLoader } from '@neo/primitives/button'
import { useIsMutating } from '@tanstack/react-query'
import { useExternalOrgAppsQuery } from 'v2/pages/settings/integrations/queries'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { KBDocumentSource, type NewKBFormValues } from '../../types'
import { getDataSourceFields } from './data-source.config'

interface DataSourceProps {
  form: GenericFormType<NewKBFormValues>
  onClick?: () => void
}

export const DataSource = ({ form, onClick }: DataSourceProps) => {
  const { data: connectedOrgApps } = useExternalOrgAppsQuery()
  // Spinner while the create is in flight; it also blocks a second click, which would create a second KB.
  const isCreating = useIsMutating({ mutationKey: flowQueryKeys().createKnowledgeBase }) > 0

  const isConnected = (provider: string) => {
    if (!connectedOrgApps) return false
    return connectedOrgApps.some((app) => app.provider === provider && app.state === 'ACTIVE')
  }

  const getButtonText = () => {
    const { type } = form.state.values
    if (type === KBDocumentSource.FILE) return 'Import'
    return 'Sync data'
  }

  return (
    <div className="flex flex-col gap-6 w-[628px] min-h-0">
      <FieldsRenderer<NewKBFormValues> form={form} fields={getDataSourceFields(onClick)} />
      <div className="flex justify-end -mt-2">
        <form.Subscribe
          selector={(state) => {
            // Check if we should hide the button for unconnected apps
            if (state.values.type === KBDocumentSource.APP && state.values.app_source) {
              const isAppConnected = isConnected(state.values.app_source)
              if (!isAppConnected) {
                return { shouldHide: true, disabled: false }
              }
            }

            // Check if we should hide the button while app is loading
            if (state.values.type === KBDocumentSource.APP && state.values.appLoading) {
              return { shouldHide: true, disabled: false }
            }

            // Check if we should hide the button while loading (for other types)
            if (state.values.isLoading) {
              return { shouldHide: true, disabled: false }
            }

            // Original disable logic for other cases
            if (state.values.type === KBDocumentSource.URL)
              return { shouldHide: true, disabled: state.values.links.length === 0 }
            if (state.values.type === KBDocumentSource.FILE)
              return { shouldHide: false, disabled: state.values.files.length === 0 }
            if (state.values.type === KBDocumentSource.APP) {
              const hasSource = !!state.values.app_source
              const hasAppData =
                state.values.url_selection_type === 'all' || state.values.app_data.length > 0
              return { shouldHide: false, disabled: !hasSource || !hasAppData }
            }
            return { shouldHide: false, disabled: true }
          }}
        >
          {({ shouldHide, disabled }) => {
            if (shouldHide) return null

            return (
              <ButtonWithLoader
                type="submit"
                data-testid="button-finish"
                disabled={disabled}
                loading={isCreating}
                onClick={() => {
                  form.validateAllFields('change').then((cause) => {
                    if (cause.length > 0) return
                    // This is for the upload functionality
                    if (onClick) {
                      onClick()
                    }
                  })
                }}
              >
                {getButtonText()}
              </ButtonWithLoader>
            )
          }}
        </form.Subscribe>
      </div>
    </div>
  )
}
